import React, {useMemo} from 'react';
import {Text, View} from 'react-native';
import {useSelector} from 'react-redux';
import {skipassClientInfoSelector} from '../../../redux/selectors/skipass';
import {Tabs, TabsProps} from '../../../shared/components/tabs';
import {useStyles} from '../../../shared/hooks/styles';
import {useIsMobile} from '../../../shared/hooks/useIsMobile';
import {useTheme} from '../../../shared/theme';
import {DeviceSize} from '../../../shared/theme/helpers';
import {formatDateTime} from '../../../shared/utils/formatDate';
import {LiftingsTable} from './LiftingsTable';
import {OperationsTable} from './OperationsTable';
import {TicketsTable} from './TicketsTable';

const unusedOperationTypes = [21];

export function OperationsTabs() {
  const isMobile = useIsMobile();
  const {clientInfo} = useSelector(skipassClientInfoSelector());
  const liftings = clientInfo?.passesInfo.items;
  const operations = clientInfo?.clientAccountInfo.moneyOperations.filter(
    (item) => !unusedOperationTypes.includes(item.operationType),
  );
  const tickets = [
    ...(clientInfo?.clientAccountInfo.singleTickets ?? []),
    ...(clientInfo?.clientAccountInfo.seasonTickets ?? []),
  ];

  const styles = useStyles(({mediaOnly, theme}) => ({
    rowContainer: {
      borderRadius: 5,
      borderWidth: 1,
      borderColor: theme.color.borderColor,
      backgroundColor: theme.color.primaryNonGreyBackground,
      shadowColor: theme.color.primaryBlackColor,
      shadowOpacity: 0.05,
      shadowOffset: {width: 0, height: 4},
      shadowRadius: 10,
      elevation: 5,
      width: '100%',
      overflow: 'hidden',
      ...mediaOnly(DeviceSize.mobile, {alignItems: 'flex-start'}),
    },

    rowTitle: {
      ...mediaOnly(
        DeviceSize.mobile,
        {
          ...theme.fonts.body,
          fontWeight: 'bold',
        },
        {...theme.fonts.h5},
      ),
    },

    rowTop: {
      paddingVertical: 10,
      paddingHorizontal: 20,
    },

    rowBottom: {
      paddingVertical: 10,
      paddingHorizontal: 20,
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      backgroundColor: '#FBFCFF',
    },

    rowDate: {
      ...theme.fonts.caption,
      fontSize: 14,
      marginTop: 10,

      ...mediaOnly(DeviceSize.mobile, {marginTop: 0, ...theme.fonts.bodySmallLight, color: '#758B98'}),
    },

    quantity: {
      ...theme.fonts.body,
    },

    amount: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 16,
      fontWeight: 'bold',

      ...mediaOnly(DeviceSize.mobile, {...theme.fonts.bodySmall}),
    },
  }));

  const theme = useTheme();

  const tabsItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'liftings',
        label: 'Подъемы',
        isEmpty: liftings == null || liftings.length === 0,
        children: isMobile ? (
          liftings?.map(({facilityName, date, payments}, index) => (
            <View
              key={date.toString()}
              style={[styles.rowContainer, index !== liftings.length - 1 ? {marginBottom: 15} : null]}>
              <View style={styles.rowTop}>
                <Text style={styles.rowTitle}>{facilityName}</Text>
              </View>
              <View style={styles.rowBottom}>
                <Text style={styles.rowDate}>{formatDateTime(date)}</Text>
                <Text style={styles.quantity}>{payments.length}</Text>
              </View>
            </View>
          ))
        ) : (
          <LiftingsTable data={liftings} />
        ),
      },
      {
        key: 'operations',
        label: 'Операции',
        isEmpty: operations == null || operations.length === 0,
        children: isMobile ? (
          operations?.map(({amount, timestamp, comment, goodsName}, index) => (
            <View
              key={timestamp + index}
              style={[styles.rowContainer, index !== operations.length - 1 ? {marginBottom: 15} : null]}>
              <View style={styles.rowTop}>
                <Text style={styles.rowTitle}>
                  {comment}, {goodsName}
                </Text>
              </View>

              <View style={styles.rowBottom}>
                <Text style={styles.rowDate}>{formatDateTime(timestamp)}</Text>
                <Text
                  style={[
                    styles.amount,
                    amount < 0 && {color: theme.skipass.redColor},
                    amount === 0 && {color: theme.skipass.neutralColor},
                    amount > 0 && {color: theme.skipass.greenColor},
                  ]}>
                  {amount < 0 ? Math.abs(amount) : `+${amount}`} ₽
                </Text>
              </View>
            </View>
          ))
        ) : (
          <OperationsTable data={operations} />
        ),
      },
      {
        key: 'tickets',
        label: 'Билеты',
        isEmpty: tickets.length === 0,
        children: isMobile ? (
          tickets?.map(({name, toIso, isActivated}, index) => (
            <View key={toIso} style={[styles.rowContainer, index !== tickets.length - 1 ? {marginBottom: 15} : null]}>
              <View style={styles.rowTop}>
                <Text style={styles.rowTitle}>{name}</Text>
              </View>

              <View style={styles.rowBottom}>
                <Text style={styles.rowDate}>до {formatDateTime(toIso)}</Text>
                <Text style={[styles.amount, {color: isActivated ? theme.skipass.greenColor : theme.skipass.redColor}]}>
                  {isActivated ? 'Активный' : 'Не активный'}
                </Text>
              </View>
            </View>
          ))
        ) : (
          <TicketsTable data={tickets} />
        ),
      },
    ],
    [liftings, operations],
  );

  return <Tabs items={tabsItems} underlineActiveTab={!isMobile} size={isMobile ? 'medium' : 'small'} />;
}
